import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const PresentationEmailPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [fromId, setFromId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/accounts").then((r) => {
      const real = (r.data || []).filter((a) => a.type !== "demo" && !a.is_demo && a.email_address);
      setAccounts(real);
      if (real[0]) setFromId(real[0].id);
    }).catch(() => {});
    api.get("/admin/presentation/template").then((r) => {
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
    }).catch(() => {});
  }, []);

  const recipientsCount = (recipients.match(/[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+/g) || []).length;

  const send = async () => {
    if (!fromId) return toast.error("Seleziona una casella mittente");
    if (recipientsCount === 0) return toast.error("Inserisci almeno un indirizzo email valido");
    setSending(true);
    try {
      const r = await api.post("/admin/presentation/send", { from_account_id: fromId, subject, body, recipients });
      let msg = `Mail inviata a ${r.data.sent} destinatari (in CCN) da ${r.data.from}`;
      if ((r.data.invalid || []).length) msg += ` · ${r.data.invalid.length} indirizzi ignorati`;
      toast.success(msg);
      setRecipients("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="presentation-email-card" className="bg-card border border-border rounded-md p-6">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="h-4 w-4 text-[#007BFF]" />
        <h3 className="font-head text-lg font-semibold">Mail di presentazione Sedio</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Invia una mail di presentazione agli amministratori per farli provare Sedio. Ogni destinatario è in
        <strong> CCN</strong> (gli altri indirizzi non sono visibili). L'invio parte dalla casella collegata che scegli.
      </p>

      <div className="grid gap-4">
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mittente (casella collegata)</label>
          <Select value={fromId} onValueChange={setFromId}>
            <SelectTrigger data-testid="presentation-sender-select" className="mt-1.5">
              <SelectValue placeholder={accounts.length ? "Scegli casella" : "Nessuna casella collegata"} />
            </SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id} data-testid={`presentation-sender-${a.id}`}>
                  {a.email_address}{a.display_name ? ` — ${a.display_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Oggetto</label>
          <Input data-testid="presentation-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" />
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Testo della mail (modificabile)</label>
          <Textarea data-testid="presentation-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="mt-1.5 font-mono text-sm" />
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Destinatari <span className="text-muted-foreground/70 normal-case font-normal">(uno per riga o separati da virgola)</span>
          </label>
          <Textarea data-testid="presentation-recipients-input" value={recipients} onChange={(e) => setRecipients(e.target.value)}
            rows={5} placeholder={"mario.rossi@studio.it\nlucia.bianchi@amministrazioni.it"} className="mt-1.5 text-sm" />
          <div className="text-xs text-muted-foreground mt-1" data-testid="presentation-recipients-count">
            {recipientsCount} indirizzi validi rilevati
          </div>
        </div>

        <div>
          <Button data-testid="presentation-send-btn" onClick={send} disabled={sending || !fromId || recipientsCount === 0}
            className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md h-10">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {sending ? "Invio in corso…" : `Invia a ${recipientsCount} destinatari`}
          </Button>
        </div>
      </div>
    </div>
  );
};
